/** Research, as the app has it. Filing a research report needs the app's research mode; until the
 * workbench exposes `research.start`, the question goes to the chosen specialist as a prompt. */

import { ChartColumn, Magnifier, Route } from '@gravity-ui/icons';
import { useState } from 'react';

import type { Workbench } from '../useWorkbench.ts';
import { Conversation, type Sent } from './Conversation.tsx';
import { Picker, type Suggestion, useBaseBranch, useComposer, WorkComposer, WorkHero, WorkStrip } from './work.tsx';

const SUGGESTIONS: Suggestion[] = [
  { tone: 'teal', icon: <Route />, text: 'Draft a roadmap from the product vision' },
  { tone: 'violet', icon: <Magnifier />, text: 'Investigate why the build is slow and list the top causes with evidence' },
  { tone: 'amber', icon: <ChartColumn />, text: 'Survey how errors are handled across the codebase and propose a convention' },
];

export function ResearchView({ w }: { w: Workbench }) {
  const branch = useBaseBranch(w);
  const solo = (w.activity.data?.agents ?? []).filter((a) => a.teamId === 'solo');
  const [picked, setPicked] = useState<string>();
  const target = solo.find((a) => a.sessionId === picked) ?? solo[0];
  const c = useComposer();
  const [sent, setSent] = useState<Sent[]>([]);

  async function send() {
    if (!target) return;
    c.setBusy(true);
    const question = c.text.trim();
    await w.act('Research', async () => {
      const started = await w.tryCall<{ conversationId?: string }>('research.start', { question, specialist: target.name });
      if (started) {
        setSent((s) => [...s, { at: Date.now(), text: question, outcome: 'sent' }]);
      } else {
        const r = await w.call<{ outcome: string }>('sessions.prompt', { sessionId: target.sessionId, text: `Research this and write it up with evidence: ${question}`, source: 'aioe-remote' });
        setSent((s) => [...s, { at: Date.now(), text: question, outcome: r?.outcome }]);
      }
      c.setText('');
    });
    c.setBusy(false);
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <WorkStrip label="Research" session={sent.length && target ? target.name : 'new session'} branch={branch} />
      {sent.length ? (
        <div className="min-h-0 flex-1 overflow-auto">
          <Conversation w={w} sessionId={target?.sessionId} sent={sent} />
        </div>
      ) : (
        <WorkHero
          tone="violet"
          title="Research"
          blurb="Ask a question worth investigating — a specialist, a team or a council researches it, and the report is filed as an artifact and saved to memory."
          suggestions={SUGGESTIONS}
          onPick={c.setText}
        />
      )}
      <WorkComposer
        text={c.text}
        setText={c.setText}
        busy={c.busy}
        canSend={w.connected && !!target}
        onSend={() => void send()}
        placeholder="Ask anything... (Enter to send · Shift+Enter for a new line)"
        lands={
          <>
            Agent work lands on <span className="mono font-medium text-foreground">{branch}</span>
          </>
        }
        pickers={
          solo.length ? (
            <Picker label="Specialist" value={target?.sessionId ?? ''} onChange={setPicked} options={solo.map((a) => ({ value: a.sessionId, label: a.name, hint: a.model }))} />
          ) : (
            <span className="text-xs text-muted">{w.connected ? 'No specialist running; open one in the app.' : 'Not connected.'}</span>
          )
        }
        footnote={target ? `${target.name} researches the whole question. Filing the report as an artifact needs the app's research mode.` : ''}
      />
    </div>
  );
}
